import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, pollJob, jobErrorMessage, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Loader2, Check, RotateCcw, Download, Archive, ImageIcon, Wand2, ShieldCheck, CheckCheck,
  Eye, History, Sparkles, DollarSign, Upload, Copy, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { mediaFileUrl, assetFileUrl } from "./constants";

function ChecksList({ v, label, testid }) {
  if (!v?.checks) return null;
  const failed = v.checks.filter((c) => !c.ok);
  return (
    <div className="text-xs mt-2" data-testid={testid}>
      <span className={`rounded-full px-2 py-0.5 ${v.valid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{label}: {v.valid ? "passed" : `${failed.length} failed`}</span>
      {failed.length > 0 && <ul className="mt-1.5 list-disc pl-4 text-red-600">{failed.slice(0, 8).map((c, i) => <li key={i}>{c.name}{c.detail ? ` (${c.detail})` : ""}</li>)}</ul>}
    </div>
  );
}

export default function MediaReview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState({ items: [], count: 0 });

  // dialogs
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ headline: "", body: "", cta: "", prompt: "", subject: "", setting: "", style: "", mood: "", colors: "", exclusions: "", instructions: "" });
  const [source, setSource] = useState("programmatic");
  const [assets, setAssets] = useState([]);
  const [reusable, setReusable] = useState([]);
  const [aiConfirm, setAiConfirm] = useState(null); // cost preview object
  const [preview, setPreview] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);
  const [versions, setVersions] = useState(null);

  const load = () => {
    api.get("/marketing/media/review").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/marketing/media/review/failed").then((r) => setFailed(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runJob = async (postFn, msg) => {
    setBusy(true);
    try {
      const { data: d } = await postFn();
      const job = await pollJob(d.job_id);
      if (job.status === "Failed" || job.status === "Cancelled") toast.error(jobErrorMessage(job));
      else toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed"); }
    finally { setBusy(false); }
  };
  const simple = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); } };

  const approve = (m) => simple(() => api.post(`/marketing/media/${m.id}/approve`), "Media approved — Ready to Schedule");
  const archive = (m) => simple(() => api.post(`/marketing/media/${m.id}/archive`), "Media archived");
  const approveAll = () => simple(() => api.post("/marketing/media/approve-all-valid-low-risk", {}), "Approved all valid low-risk media");

  const openEdit = (m) => {
    setEditing(m); setSource("programmatic");
    const cd = m.creative_direction || {};
    setForm({
      headline: m.spec?.headline || "", body: m.spec?.body || "", cta: m.spec?.cta || "",
      prompt: cd.prompt || "", subject: cd.subject || "", setting: cd.setting || "", style: cd.style || "",
      mood: cd.mood || "", colors: cd.colors || "", exclusions: cd.exclusions || "", instructions: cd.instructions || "",
    });
    api.get("/marketing/brand-assets?include_inactive=false").then((r) => setAssets((r.data || []).filter((a) => a.storage_path && !["Font", "Brand color", "Disclaimer text"].includes(a.asset_type)))).catch(() => {});
    api.get("/marketing/media/reusable").then((r) => setReusable(r.data.items || [])).catch(() => {});
  };

  const editBody = () => ({
    display_text: { headline: form.headline, body: form.body, cta: form.cta },
    creative: { prompt: form.prompt, subject: form.subject, setting: form.setting, style: form.style, mood: form.mood, colors: form.colors, exclusions: form.exclusions, instructions: form.instructions },
  });

  const saveDraft = async () => {
    const m = editing;
    await simple(() => api.post(`/marketing/media/${m.id}/edit-creative`, { ...editBody(), rerender: false }), "Draft saved (not re-rendered)");
  };
  const rerender = async () => {
    const m = editing; setEditing(null);
    await runJob(() => api.post(`/marketing/media/${m.id}/edit-creative`, { ...editBody(), rerender: true }, idemHeaders(newIdemKey())), "Re-rendered (no AI charge)");
  };
  const saveTextFirst = async (m) => { await api.post(`/marketing/media/${m.id}/edit-creative`, { ...editBody(), rerender: false }); };
  const chooseAsset = async (assetId) => {
    const m = editing; setEditing(null);
    await runJob(async () => { await saveTextFirst(m); return api.post(`/marketing/media/${m.id}/select-asset`, { asset_id: assetId }, idemHeaders(newIdemKey())); }, "Brand asset applied (no AI charge)");
  };
  const doReuse = async (srcId) => {
    const m = editing; setEditing(null);
    await runJob(async () => { await saveTextFirst(m); return api.post(`/marketing/media/${m.id}/reuse-media`, { source_media_id: srcId }, idemHeaders(newIdemKey())); }, "Reused media applied (no AI charge)");
  };
  const doUpload = async (file) => {
    const m = editing;
    const reader = new FileReader();
    reader.onload = async () => {
      setEditing(null);
      await runJob(async () => { await saveTextFirst(m); return api.post(`/marketing/media/${m.id}/upload-image`, { image_base64: reader.result, mime: file.type }, idemHeaders(newIdemKey())); }, "Uploaded image applied (no AI charge)");
    };
    reader.readAsDataURL(file);
  };
  const openAiConfirm = async () => {
    try { const { data: cost } = await api.get("/marketing/media/ai-cost-preview"); setAiConfirm(cost); }
    catch { toast.error("Could not load cost preview"); }
  };
  const confirmAi = async () => {
    const m = editing; setAiConfirm(null); setEditing(null);
    await runJob(async () => { await saveTextFirst(m); return api.post(`/marketing/media/${m.id}/generate-ai-background`, { prompt: form.prompt, confirm: true }, idemHeaders(newIdemKey())); }, "AI background generated");
  };

  const openVersions = (m) => { setVersionsFor(m); setVersions(null); api.get(`/marketing/media/${m.id}/versions`).then((r) => setVersions(r.data)).catch(() => setVersions(false)); };
  const restore = (n) => simple(async () => { await api.post(`/marketing/media/${versionsFor.id}/restore-version/${n}`); setVersionsFor(null); }, `Restored version ${n}`);

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (data === false) return <p className="text-sm text-red-600">Failed to load Media Review.</p>;

  const feedOf = (m) => (m.outputs || []).filter((o) => o.size_label === "feed").sort((a, b) => a.slide_index - b.slide_index);

  return (
    <div data-testid="media-review-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold">Media Review</h2>
          <p className="text-sm text-gray-500 mt-1">Review branded graphics before scheduling. {data.count} awaiting review.</p>
        </div>
        <Button onClick={approveAll} disabled={busy || data.count === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-all-valid-button"><CheckCheck className="h-4 w-4 mr-2" /> Approve All Valid Low-Risk Media</Button>
      </div>

      {data.count === 0 ? (
        <div className="mt-6 bg-white border border-dashed border-gray-200 rounded-xl px-6 py-12 text-center text-gray-400 text-sm" data-testid="media-review-empty">No media awaiting review. Use "Create Media" on an approved content item.</div>
      ) : (
        <div className="mt-6 space-y-6">
          {data.items.map((m) => {
            const feed = feedOf(m);
            return (
              <div key={m.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`media-card-${m.id}`}>
                <div className="flex flex-wrap gap-5">
                  <div className="flex gap-2 overflow-x-auto max-w-full">
                    {feed.length === 0 && <div className="h-52 w-40 bg-gray-50 flex items-center justify-center rounded-lg"><ImageIcon className="h-8 w-8 text-gray-300" /></div>}
                    {feed.map((o) => (
                      <img key={o.storage_path} src={mediaFileUrl(o.storage_path)} alt="media" className="h-52 rounded-lg border border-gray-100" data-testid={`media-preview-${m.id}-${o.slide_index}`} />
                    ))}
                  </div>
                  <div className="flex-1 min-w-[260px]">
                    <p className="font-heading font-bold">{m.content?.title || "Content"}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px]">
                      <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">{m.template}</span>
                      <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">{m.platform}</span>
                      <span className="rounded-full bg-[#F3E8FF] text-[#6B21A8] px-2 py-0.5" data-testid={`media-method-${m.id}`}>{m.method || "Programmatic Template"}</span>
                      <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5" data-testid={`media-version-${m.id}`}>v{m.version || 1}</span>
                      {m.campaign_name && <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">{m.campaign_name}</span>}
                    </div>
                    <div className="mt-2 text-sm text-gray-700 space-y-1">
                      <p><span className="text-gray-400">Caption:</span> {m.content?.caption || "—"}</p>
                      <p><span className="text-gray-400">CTA:</span> {m.content?.cta || "—"}</p>
                      <p className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-[#581C87]" /> Compliance: <span className={`rounded-full px-2 py-0.5 text-xs ${m.content?.compliance?.result === "Blocked" ? "bg-red-100 text-red-700" : m.content?.compliance?.result === "Warning" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{m.content?.compliance?.result || "—"}</span></p>
                      <p><span className="text-gray-400">Supporting image:</span> {m.supporting_image?.source || "none"}{m.supporting_image?.source === "generated" ? ` (${m.supporting_image?.model})` : ""}</p>
                    </div>
                    <ChecksList v={m.validation} label="Validation" testid={`validation-list-${m.id}`} />
                    <ChecksList v={m.legibility} label="Legibility" testid={`legibility-list-${m.id}`} />
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Button onClick={() => approve(m)} disabled={busy || !m.validation?.valid} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-media-${m.id}`}><Check className="h-4 w-4 mr-1.5" /> Approve Media</Button>
                      <Button onClick={() => setPreview(m)} disabled={busy} variant="outline" className="border-gray-200" data-testid={`preview-media-${m.id}`}><Eye className="h-4 w-4 mr-1.5" /> Preview</Button>
                      <Button onClick={() => openEdit(m)} disabled={busy} variant="outline" className="border-[#581C87] text-[#581C87]" data-testid={`edit-creative-${m.id}`}><Wand2 className="h-4 w-4 mr-1.5" /> Edit Creative</Button>
                      <Button onClick={() => openVersions(m)} disabled={busy} variant="outline" className="border-gray-200" data-testid={`versions-media-${m.id}`}><History className="h-4 w-4 mr-1.5" /> Versions ({m.version_count || 0})</Button>
                      {feed[0] && <a href={mediaFileUrl(feed[0].storage_path)} download target="_blank" rel="noreferrer" data-testid={`download-media-${m.id}`}><Button variant="outline" className="border-gray-200"><Download className="h-4 w-4 mr-1.5" /> Download</Button></a>}
                      <Button onClick={() => archive(m)} disabled={busy} variant="outline" className="border-gray-200 text-amber-600" data-testid={`archive-media-${m.id}`}><Archive className="h-4 w-4 mr-1.5" /> Archive</Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {failed.count > 0 && (
        <div className="mt-8" data-testid="failed-media-section">
          <h3 className="font-heading text-lg font-bold text-red-700 flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Failed Media ({failed.count})</h3>
          <p className="text-sm text-gray-500 mt-1">These stay visible until edited, retried, returned to content, or archived. Legibility failures need an edit — a plain retry will fail again.</p>
          <div className="mt-3 space-y-3">
            {failed.items.map((f) => (
              <div key={f.id} className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid={`failed-media-${f.id}`}>
                <p className="font-semibold text-sm">{f.post_title || "Content"}</p>
                <p className="text-xs text-gray-600 mt-0.5">{f.platform || "—"} · {f.campaign || "—"} · Failed {f.failed_at ? new Date(f.failed_at).toLocaleString() : ""}</p>
                <p className="text-sm text-red-700 mt-2" data-testid={`failed-reason-${f.id}`}>{f.reason}</p>
                <p className="text-xs text-gray-500 mt-1">Media usage charged: {f.usage_charged ? "Yes" : "No"}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={() => runJob(() => api.post(`/marketing/media/${f.id}/regenerate-graphic`, null, idemHeaders(newIdemKey())), "Media retried")} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid={`retry-failed-${f.id}`}><RotateCcw className="h-4 w-4 mr-1.5" /> Retry Media</Button>
                  <Button onClick={() => simple(() => api.post(`/marketing/content/${f.content_id}/return-to-draft`), "Returned to content")} disabled={busy} variant="outline" className="border-gray-200" data-testid={`return-content-${f.id}`}>Return to Content</Button>
                  <Button onClick={() => simple(() => api.post(`/marketing/media/${f.id}/archive`), "Failed media archived")} disabled={busy} variant="outline" className="border-gray-200 text-amber-600" data-testid={`archive-failed-${f.id}`}><Archive className="h-4 w-4 mr-1.5" /> Archive</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Creative dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="edit-creative-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Creative</DialogTitle>
            <DialogDescription>Control the exact overlay text and visual direction. Text is rendered deterministically — the AI never writes final promotional text. Preview and drafts never call OpenAI.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">Display Text (overlay)</p>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Headline</Label><Input value={form.headline} onChange={(e) => setForm((s) => ({ ...s, headline: e.target.value }))} data-testid="edit-headline" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Supporting line(s)</Label><Textarea value={form.body} onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))} data-testid="edit-body" /></div>
                <div className="space-y-1.5"><Label className="text-xs">CTA</Label><Input value={form.cta} onChange={(e) => setForm((s) => ({ ...s, cta: e.target.value }))} data-testid="edit-cta" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">Visual Direction</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[["subject", "Subject"], ["setting", "Setting"], ["style", "Style"], ["mood", "Mood"], ["colors", "Colors"], ["exclusions", "Exclusions"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label><Input value={form[k]} onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`edit-${k}`} /></div>
                ))}
                <div className="space-y-1.5 md:col-span-2"><Label className="text-xs">Additional instructions</Label><Textarea value={form.instructions} onChange={(e) => setForm((s) => ({ ...s, instructions: e.target.value }))} data-testid="edit-instructions" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">Image Source</p>
              <RadioGroup value={source} onValueChange={setSource} className="grid grid-cols-1 gap-2" data-testid="source-radio-group">
                {[
                  ["programmatic", "Programmatic Template — No AI image charge", ImageIcon],
                  ["ai", "Generate AI Background — Uses OpenAI Image Generation (paid)", Sparkles],
                  ["asset", "Choose Brand Asset", Layers],
                  ["upload", "Upload My Own Image", Upload],
                  ["reuse", "Reuse Existing Approved Media", Copy],
                ].map(([val, label, Icon]) => (
                  <label key={val} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer ${source === val ? "border-[#581C87] bg-[#F3E8FF]" : "border-gray-200"}`} data-testid={`source-${val}`}>
                    <RadioGroupItem value={val} /><Icon className="h-4 w-4 text-[#581C87]" /><span className="text-sm">{label}</span>
                  </label>
                ))}
              </RadioGroup>

              {source === "ai" && (
                <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <Label className="text-xs">Custom AI image prompt (background only — no text is rendered by AI)</Label>
                  <Textarea value={form.prompt} onChange={(e) => setForm((s) => ({ ...s, prompt: e.target.value }))} placeholder="e.g. abstract royal-purple trichology motif with negative space" data-testid="edit-ai-prompt" />
                  <Button onClick={openAiConfirm} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-ai-confirm-button"><DollarSign className="h-4 w-4 mr-1.5" /> Generate AI Background (Paid)</Button>
                </div>
              )}
              {source === "asset" && (
                <div className="mt-3 grid grid-cols-3 gap-2 max-h-56 overflow-y-auto" data-testid="asset-picker">
                  {assets.length === 0 && <p className="text-xs text-gray-400 col-span-3">No approved brand assets available.</p>}
                  {assets.map((a) => (
                    <button key={a.id} onClick={() => chooseAsset(a.id)} disabled={busy} className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#581C87]" data-testid={`pick-asset-${a.id}`}>
                      <img src={assetFileUrl(a.storage_path)} alt={a.title} className="h-24 w-full object-cover" />
                      <p className="text-[10px] text-gray-500 truncate px-1 py-0.5">{a.asset_type}</p>
                    </button>
                  ))}
                </div>
              )}
              {source === "upload" && (
                <div className="mt-3">
                  <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} data-testid="edit-upload-input" />
                </div>
              )}
              {source === "reuse" && (
                <div className="mt-3 grid grid-cols-3 gap-2 max-h-56 overflow-y-auto" data-testid="reuse-picker">
                  {reusable.length === 0 && <p className="text-xs text-gray-400 col-span-3">No reusable media available.</p>}
                  {reusable.filter((r) => r.id !== editing?.id).map((r) => (
                    <button key={r.id} onClick={() => doReuse(r.id)} disabled={busy} className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#581C87]" data-testid={`pick-reuse-${r.id}`}>
                      {r.preview ? <img src={mediaFileUrl(r.preview)} alt="reuse" className="h-24 w-full object-cover" /> : <div className="h-24 bg-gray-50" />}
                      <p className="text-[10px] text-gray-500 truncate px-1 py-0.5">{r.source}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)} data-testid="edit-cancel-button">Cancel</Button>
            <Button variant="outline" onClick={saveDraft} disabled={busy} className="border-gray-200" data-testid="save-draft-button">Save Draft</Button>
            <Button onClick={rerender} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="rerender-button">Rerender (Programmatic)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paid AI confirm */}
      <Dialog open={!!aiConfirm} onOpenChange={(o) => !o && setAiConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="ai-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><DollarSign className="h-4 w-4 text-amber-600" /> Confirm Paid Generation</DialogTitle>
            <DialogDescription>{aiConfirm?.message}</DialogDescription></DialogHeader>
          <div className="text-sm text-gray-700 space-y-1 py-2">
            <p><span className="text-gray-400">Charge:</span> {aiConfirm?.charge}</p>
            <p><span className="text-gray-400">This month:</span> {aiConfirm?.image_used}/{aiConfirm?.image_limit} used · {aiConfirm?.remaining} remaining</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAiConfirm(null)}>Cancel</Button>
            <Button onClick={confirmAi} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-ai-generate-button">Generate — I understand this is paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mobile Feed Preview</DialogTitle>
            <DialogDescription>This preview never publishes, schedules, approves, or sends to Buffer.</DialogDescription></DialogHeader>
          {preview && (
            <div className="mx-auto w-[300px] rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#581C87] to-[#7C3AED]" />
                <span className="text-xs font-semibold">{preview.platform === "Facebook" ? "KISSED University" : "@kisseduniversity"}</span>
                <span className="ml-auto text-[10px] text-gray-400">{preview.platform}</span>
              </div>
              {feedOf(preview)[0] && <img src={mediaFileUrl(feedOf(preview)[0].storage_path)} alt="preview" className="w-full" data-testid="preview-image" />}
              <div className="px-3 py-2 text-xs">
                <p className="line-clamp-3"><span className="font-semibold">{preview.platform === "Facebook" ? "KISSED University" : "@kisseduniversity"}</span> {preview.content?.caption}</p>
                {preview.content?.cta && <p className="mt-1 text-[#581C87] font-medium">{preview.content.cta}</p>}
                {preview.content?.landing_page_url && <p className="mt-1 text-gray-400 truncate">{preview.content.landing_page_url}</p>}
              </div>
              <div className="px-3 py-2 border-t border-gray-100 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-[#F3E8FF] text-[#6B21A8] px-2 py-0.5 text-[10px]">{preview.method || "Programmatic Template"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${preview.supporting_image?.source === "generated" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{preview.supporting_image?.source === "generated" ? "Paid AI background" : "No AI charge"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${preview.validation?.valid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>Validation {preview.validation?.valid ? "passed" : "failed"}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${preview.legibility?.valid ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>Legibility {preview.legibility?.valid ? "passed" : "failed"}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Version history */}
      <Dialog open={!!versionsFor} onOpenChange={(o) => !o && setVersionsFor(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="versions-dialog">
          <DialogHeader><DialogTitle className="font-heading">Media Version History</DialogTitle>
            <DialogDescription>Every render, generation, upload and restore is kept. Restoring does not change any post already scheduled or published in Buffer.</DialogDescription></DialogHeader>
          {versions === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#581C87]" /></div>
            : versions === false ? <p className="text-sm text-red-600">Failed to load versions.</p>
              : (
                <div className="space-y-2">
                  {(versions.items || []).slice().reverse().map((v) => (
                    <div key={v.version} className="flex items-center gap-3 border border-gray-200 rounded-lg p-2" data-testid={`version-${v.version}`}>
                      {v.preview ? <img src={mediaFileUrl(v.preview)} alt="v" className="h-16 w-12 object-cover rounded" /> : <div className="h-16 w-12 bg-gray-50 rounded" />}
                      <div className="flex-1 text-xs">
                        <p className="font-semibold">v{v.version} · {v.method} {v.version === versions.current_version && <span className="text-emerald-600">(current)</span>}</p>
                        <p className="text-gray-500">{v.created_at ? new Date(v.created_at).toLocaleString() : ""} · {v.actor}</p>
                        <p className="text-gray-500">{v.display_text?.headline}</p>
                        <p><span className={v.valid ? "text-emerald-600" : "text-red-600"}>validation {v.valid ? "ok" : "fail"}</span> · <span className={v.legibility_valid ? "text-emerald-600" : "text-red-600"}>legibility {v.legibility_valid ? "ok" : "fail"}</span> · {v.usage_charged ? "paid AI" : "no charge"}</p>
                      </div>
                      {v.version !== versions.current_version && <Button onClick={() => restore(v.version)} disabled={busy} size="sm" variant="outline" className="border-gray-200" data-testid={`restore-version-${v.version}`}>Restore</Button>}
                    </div>
                  ))}
                  {(versions.items || []).length === 0 && <p className="text-sm text-gray-400 text-center py-6">No versions yet.</p>}
                </div>
              )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
